import * as React from 'react';
import { DatasheetGetDto } from '@common/dto/datasheet/get-dto';
import { useTabsController } from './tabs';
import { DatasheetElement, DatasheetElementId, DatasheetElementType } from '@modules/datasheets/types/datasheet-element';
import { DatasheetFieldComponentGetDto } from '@common/dto/datasheet/field-component/get-dto';
import { FieldType } from '@common/dto/field/type';
import { DatasheetFieldComponentHeadingPosition } from '@common/dto/datasheet/field-component/heading-position';
import { DatasheetFieldComponentTextAlign } from '@common/dto/datasheet/field-component/text-align';
import { DatasheetGridSettingsMarginType } from '@common/dto/datasheet/grid-settings-dto';
import { DatasheetStorage } from '@modules/datasheets/utils/datasheet-storage';
import { DatasheetTextComponentGetDto } from '@common/dto/datasheet/text-component/get-dto';
import { useDatasheetUndoRedoController } from './undo-redo';
import { generateId } from '@common/utils/common';
import { DatasheetTabDto } from '@common/dto/datasheet/tab-dto';

type Props = {
  datasheet: DatasheetGetDto;
  onDatasheetChange: (datasheet: DatasheetGetDto) => void;
};

export const useDatasheetController = (props: Props) => {
  const { datasheet, onDatasheetChange } = props;
  const [currentTabId, setCurrentTabId] = React.useState(datasheet.tabs[0]?.id);
  const tabsController = useTabsController({ datasheet, onDatasheetChange });
  const [datasheetElements, setDatasheetElements] = React.useState<Record<DatasheetElementId, DatasheetElement>>({});
  const datasheetElementsStorage = React.useMemo<DatasheetStorage>(() => new DatasheetStorage(), []);
  const [currentDatasheetElementsHistoryRecordId, setCurrentDatasheetElementsHistoryRecordId] = React.useState<string>();
  const undoRedoController = useDatasheetUndoRedoController({
    currentHistoryRecordId: currentDatasheetElementsHistoryRecordId,
    getDatasheetElementsHistorySequence: () => datasheetElementsStorage.getHistorySequence(currentTabId!),
  });

  const setTabDatasheetElements = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>, tabId?: DatasheetTabDto['id']) => {
    datasheetElementsStorage.setElements(tabId ?? currentTabId!, elements);
    setDatasheetElements(elements);
  }, [currentTabId]);

  const addNewDatasheetElementsHistoryRecord = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>, tabId?: DatasheetTabDto['id']) => {
    const newHistoryRecordId = generateId();

    datasheetElementsStorage.addHistoryRecord({
      tabId: tabId ?? currentTabId!,
      currentHistoryRecordId: currentDatasheetElementsHistoryRecordId!,
      newHistoryRecordId,
      elementsForNewHistoryRecord: elements
    });

    setCurrentDatasheetElementsHistoryRecordId(newHistoryRecordId);
  }, [currentTabId, currentDatasheetElementsHistoryRecordId]);

  const setTabDatasheetElementsAndAddHistoryRecord = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>, tabId?: DatasheetTabDto['id']) => {
    setTabDatasheetElements(elements, tabId);
    addNewDatasheetElementsHistoryRecord(elements, tabId);
  }, [setTabDatasheetElements, addNewDatasheetElementsHistoryRecord]);

  const initializeDatasheetElements = React.useCallback((datasheet?: DatasheetGetDto) => {
    const fakeElements: Record<DatasheetElementId, DatasheetElement> = Array.from(Array(3)).reduce((acc, _, index) => {
      const computedCoordinates = {
        x: (index * 100) + 40,
        y: (index * 100) + 40,
      };
      const width = 100;
      const height = 100;

      const isFieldComponent = !Math.round(Math.random());

      
      const element = {
        id: index.toString(),
        type: isFieldComponent ? DatasheetElementType.field : DatasheetElementType.text,
        width: isFieldComponent ? width : 225,
        height: isFieldComponent ? height: 40,
        coordinates: computedCoordinates,
        original: {
          id: index,
          ...computedCoordinates,
          text: isFieldComponent ? undefined : 'Individual Name ' + index,
          width,
          height,
          styleFontName: '',
          styleFontSize: 0,
          field: {
            fieldid: index,
            name: 'Field ' + index,
            context: 0,
            type: FieldType.text,
            system: false,
            dssubfields: [],
            imageURL: 'gif/sysheadpeg.gif',
            options: [
              { actualValue: "AA", displayValue: "Armed Forces Americas (except Canada)" },
              { actualValue: "AB", displayValue: "Alberta" },
              { actualValue: "AK", displayValue: "Alaska" },
              ...Array.from({ length: 30 }, (_, index) => ({ actualValue: `F-${index}`, displayValue: `Value ${index}` })),
            ]
            // TODO: Update value on BE integration
          },
          m_ds_subfields: [],
          editable: true,
          tabid: 0,
          fontsize: isFieldComponent ? 0 : 20,
          fontname: 'Tahoma',
          fontcolor: isFieldComponent ? 0 : '#006699',
          textstyle: [],
          gid: 0,
          taborder: 0,
          showborder: true,
          headpos: DatasheetFieldComponentHeadingPosition.top,
          headalign: DatasheetFieldComponentTextAlign.left,
          headfont: '',
          headfontsize: 12,
          headcolor: 0,
          headstyle: [],
          contentalign: DatasheetFieldComponentTextAlign.left,
          showfieldtype: true,
          dropdownedit: false,
          headtext: 'Individual Name ' + index,
          headdisplay: true,
          theme: {},
          helptext: 'Help Text',
        }
      } as unknown as DatasheetElement<DatasheetFieldComponentGetDto> | DatasheetElement<DatasheetTextComponentGetDto>;

      acc[index] = element;

      return acc;
    }, {});

    return fakeElements;
  }, []);

  React.useEffect(() => {
    if (datasheet.tabs.length) {
      const selectedTab = datasheet.tabs[0];

      if (!selectedTab) return;

      setCurrentTabId(selectedTab.id);
      const els = initializeDatasheetElements();
      datasheet.tabs.forEach(tab => {
        datasheetElementsStorage.addTab(tab.id);

        // TODO add elements to storage
      });
      
      setTabDatasheetElementsAndAddHistoryRecord(els, selectedTab.id);
    }
  }, []);

  const handleSelectedTabChange = (tabId: DatasheetGetDto['id']) => {
    if (currentTabId && currentDatasheetElementsHistoryRecordId) {
      datasheetElementsStorage.setCurrentHistoryRecordId(currentTabId, currentDatasheetElementsHistoryRecordId);
    }

    setCurrentTabId(tabId);
    setTabDatasheetElements(datasheetElementsStorage.getElements(tabId) || {}, tabId);

    setCurrentTabId(tabId);
    const currentHistoryRecordOfNewlySelectedTabId = datasheetElementsStorage.getCurrentHistoryRecordId(tabId);
    const defaultElements = currentHistoryRecordOfNewlySelectedTabId
       ? datasheetElementsStorage.getHistoryRecord(tabId, currentHistoryRecordOfNewlySelectedTabId)
       : datasheetElementsStorage.getElements(tabId);

    const fakeElements = initializeDatasheetElements();
    const TODOREMOVE_tabisempty = Object.keys(datasheetElementsStorage.getElements(tabId) || {}).length === 0;
    const newElements = TODOREMOVE_tabisempty 
    ? fakeElements
    : defaultElements || {};
    setTabDatasheetElements(newElements, tabId);

    if (currentHistoryRecordOfNewlySelectedTabId) {
      setCurrentDatasheetElementsHistoryRecordId(currentHistoryRecordOfNewlySelectedTabId);
    } else {
      addNewDatasheetElementsHistoryRecord(newElements, tabId);
    }
  };

  const handleDatasheetElementsUndo = React.useCallback(() => {
    const currentHistoryRecordId = currentDatasheetElementsHistoryRecordId;
    const historySequence = datasheetElementsStorage.getHistorySequence(currentTabId!);

    if (!currentHistoryRecordId || !historySequence) {
      return;
    }

    const currentHistoryIndex = historySequence.indexOf(currentHistoryRecordId);
    const previousHistoryRecordId = historySequence[currentHistoryIndex - 1];

    if (!previousHistoryRecordId) {
      return;
    }

    const previousHistoryRecord = datasheetElementsStorage.getHistoryRecord(currentTabId!, previousHistoryRecordId);

    if (!previousHistoryRecord) {
      return;
    }

    setTabDatasheetElements({ ...previousHistoryRecord });
    setCurrentDatasheetElementsHistoryRecordId(previousHistoryRecordId);
  }, [currentDatasheetElementsHistoryRecordId, currentTabId, setTabDatasheetElements]);

  const handleDatasheetElementsRedo = React.useCallback(() => {
    const currentHistoryRecordId = currentDatasheetElementsHistoryRecordId;
    const historySequence = datasheetElementsStorage.getHistorySequence(currentTabId!);

    if (!currentHistoryRecordId || !historySequence) {
      return;
    }

    const currentHistoryIndex = historySequence.indexOf(currentHistoryRecordId);
    const nextHistoryRecordId = historySequence[currentHistoryIndex + 1];

    if (!nextHistoryRecordId) {
      return;
    }

    const nextHistoryRecord = datasheetElementsStorage.getHistoryRecord(currentTabId!, nextHistoryRecordId);

    if (!nextHistoryRecord) {
      return;
    }

    setTabDatasheetElements({ ...nextHistoryRecord });
    setCurrentDatasheetElementsHistoryRecordId(nextHistoryRecordId);
  }, [currentDatasheetElementsHistoryRecordId, currentTabId, setTabDatasheetElements]);


  const getDatasheetElement = React.useCallback((id: DatasheetElementId) => {
    return datasheetElements[id];
  }, [datasheetElements]);

  const updateDatasheetElement = React.useCallback((id: DatasheetElementId, data: DatasheetElement) => {
    const datasheetElement = getDatasheetElement(id);

    if (!datasheetElement) {
      return;
    }
    
    const nextDatasheetElements = {
      ...datasheetElements,
      [id]: data,
    };

    setTabDatasheetElementsAndAddHistoryRecord(nextDatasheetElements);

    return nextDatasheetElements;
  }, [
    getDatasheetElement, 
    datasheetElements, 
    setTabDatasheetElementsAndAddHistoryRecord, 
    currentTabId,
  ]);

  const datasheetElementsProcessed = React.useMemo(() => {
    return Object.values(datasheetElements);
  }, [datasheetElements]);

  const pageBreaks = {
    show: datasheet.gridSettings.showPageBreaks,
    height: 1015,
    width: 582,
  };

  const gridSettingsMargin = React.useMemo(() => {
    const dimensions: Record<DatasheetGridSettingsMarginType, { width: number; height: number }> = {
      [DatasheetGridSettingsMarginType.fhq]: {
        width: 1100,
        height: 450,
      },
      [DatasheetGridSettingsMarginType.tablet]: {
        width: 300,
        height: 650,
      },
      [DatasheetGridSettingsMarginType.custom]: {
        width: datasheet.gridSettings.marginWidth,
        height: datasheet.gridSettings.marginHeight
      },
    };

    if (datasheet.gridSettings.marginType) {
      return {
        show: datasheet.gridSettings.showMargins,
        ...dimensions[datasheet.gridSettings.marginType],
      };
    }

    return {
      show: false,
      width: 0,
      height: 0,
    };
  }, [datasheet.gridSettings]);

  return {
    updateDatasheetElement,
    currentTabId,
    handleSelectedTabChange,
    datasheetElementsProcessed,
    gridSettingsMargin,
    pageBreaks,
    ...tabsController,
    ...undoRedoController,
    handleDatasheetElementsRedo,
    handleDatasheetElementsUndo,
  };
};